import { fetchAuthSession } from "@aws-amplify/auth";
import axios from "axios";
import { cognitoSignOut } from "./aws/cognito";

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

/* ================================
   REQUEST INTERCEPTOR
================================ */

api.interceptors.request.use(async (config) => {
  const session = await fetchAuthSession();
  const cognitoToken = session.tokens?.idToken?.toString();
  const appToken = localStorage.getItem("apiAccessToken");

  if (cognitoToken) {
    config.headers["Authorization"] = `Bearer ${cognitoToken}`;
  }

  if (appToken) {
    config.headers["X-App-Token"] = appToken;
  }

  return config;
});

/* ================================
   RESPONSE INTERCEPTOR
================================ */

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;

      const refreshToken = localStorage.getItem("apiRefreshToken");
      const isRefreshRequest = original.url?.includes("/identity/refresh");

      if (refreshToken && !isRefreshRequest) {
        try {
          const { data } = await api.post("/identity/refresh", {
            refreshToken,
          });

          localStorage.setItem("apiAccessToken", data.accessToken);
          original.headers["X-App-Token"] = data.accessToken;

          return api(original);
        } catch {}
        localStorage.removeItem("apiAccessToken");
        localStorage.removeItem("apiRefreshToken");
        localStorage.removeItem("user");
        localStorage.removeItem("perfilAtivo");
        localStorage.removeItem("userName");
        sessionStorage.clear();

        await cognitoSignOut();
        window.location.href = "/";
      }
    }

    return Promise.reject(error);
  },
);

/* ================================
   AUTH CALLS
================================ */

export async function apiSignIn(email: string, password: string) {
  const response = await api.post("/identity/login", { email, password });

  localStorage.setItem("apiAccessToken", response.data.accessToken);
  localStorage.setItem("apiRefreshToken", response.data.refreshToken);

  return response.data;
}

export interface SignUpData {
  name: string;
  email: string;
  datanascimento: string;
  phoneNumber: string;
  cpf: string;
  password: string;
}

export async function apiSignUp(data: SignUpData) {
  const response = await api.post("/identity/create", {
    name: data.name,
    email: data.email,
    birthdate: data.datanascimento,
    phoneNumber: data.phoneNumber,
    cpf: data.cpf,
    password: data.password,
  });

  return response.data;
}

export async function apiLogout() {
  const refreshToken = localStorage.getItem("apiRefreshToken");

  if (refreshToken) {
    await api.post("/identity/logout", { refreshToken }).catch(() => {});
  }

  localStorage.removeItem("apiAccessToken");
  localStorage.removeItem("apiRefreshToken");
  localStorage.removeItem("user");
  localStorage.removeItem("perfilAtivo");
  localStorage.removeItem("userName");
  sessionStorage.clear();

  await cognitoSignOut();

  window.location.href = "/";
}

export async function apiRefresh() {
  const refreshToken = localStorage.getItem("apiRefreshToken");

  if (refreshToken) {
    try {
      const response = await api.post("/identity/refresh", { refreshToken });
      localStorage.setItem("apiAccessToken", response.data.accessToken);
      localStorage.setItem("apiRefreshToken", response.data.refreshToken);
    } catch {}
  }
}

export async function apiForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await api.post("/identity/password/reset", { email, code, newPassword });
}

export default api;
